function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { adBillingMode } from "../../functions/tenant-features";
import { withRouter } from "react-router";
import { CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Search, Filters, Segmented, Pill, Empty, HBars, Pending } from "../../components/analytics-kit";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import ServiceFactory from "../../service";
import RangePicker from "../../components/range-picker";
import { RANGES, num, rangeLabelFor, resolveBusinessModel, isCustomRange, parseCustomRange } from "../sales/territory-common";
import { getGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import { rangeDates } from "../../components/tenant-modules";

/*
 * Social Stats — organic social monitoring (SOCIAL_STATS.md §7; mockup
 * social-stats.html). Marketing menu group, feature-gated `socialStats`.
 *
 * Endpoints (SocialController, AnalyticsService — probed live 2026-08-10):
 *   GET /v1/social/accounts                      {items:[{platform,handle,displayName,status,lastSync,…}]}
 *   GET /v1/social/overview/{range}              {followers,followersDelta,reach,impressions,engagements,
 *                                                 videoViews,engagementRate,avgReviewRating,reviewCount}
 *   GET /v1/social/platforms/{range}             {items:[per-platform rollups]}
 *   GET /v1/social/posts/{range}?$top=           {items:[post × metrics]}
 *   GET /v1/social/reviews/locations/{range}     {items:[per-showroom rollups]}
 *   GET /v1/social/reviews?locationRef&$top=     {items:[review rows]}
 *
 * No competitors endpoint exists yet (DataForSEO SoV — spec P1 benchmark,
 * backend pending) — the Competitors tab is deliberately absent until it
 * does. Data lands via the managed-pilot token-paste (platform side); this
 * surface renders honest zero/empty states until accounts connect — which
 * is also what the Meta App Review screencast needs to show working.
 *
 * Token-death rule (spec §2): any account with status !== connected surfaces
 * as an attention nudge at the top — never silently stale data.
 */

function svc(api) {
  const Service = ServiceFactory.getType(api, "product"); // /v1 root
  return new Service();
}
async function safe(promise, fallback) {
  try {
    return (await promise) || fallback;
  } catch (e) {
    return fallback;
  }
}
// responses may arrive camel or Pascal — case-tolerant accessor
function g(o, key) {
  if (!o) return undefined;
  if (o[key] !== undefined) return o[key];
  return o[key.charAt(0).toUpperCase() + key.slice(1)];
}
// The API returns engagementRate already as a percent (13.04 = 13.04%), so a
// real rate under 1% (0.6) must not be multiplied up to 60%.
function pct(v) {
  const n = Number(v) || 0;
  return `${n.toFixed(1)}%`;
}
// Engagement rate is only meaningful with a real denominator; when reach and
// impressions are both zero the server value explodes (e.g. 2830.8%). Guard it.
function engRate(o) {
  if (!o || g(o, "engagementRate") == null) return "—";
  const reach = Number(g(o, "reach")) || 0;
  const impr = Number(g(o, "impressions")) || 0;
  if (reach <= 0 && impr <= 0) return "—";
  return pct(g(o, "engagementRate"));
}
// Facebook stopped reporting page reach in 2025: a 0 next to real engagement or
// video views reads as broken, so say it isn't reported (rate falls back to followers).
function reachCell(p) {
  const reach = Number(g(p, "reach")) || 0;
  const active = (Number(g(p, "engagements")) || 0) > 0 || (Number(g(p, "videoViews")) || 0) > 0 || (Number(g(p, "impressions")) || 0) > 0;
  if (reach <= 0 && active) return /*#__PURE__*/React.createElement("span", {
    className: "dim",
    title: "This platform no longer reports reach; its engagement rate uses followers instead"
  }, "Not reported");
  return num(g(p, "reach"));
}
function stars(v) {
  const n = Number(v) || 0;
  return n > 0 ? `${n.toFixed(1)}★` : "—";
}
const PLATFORM_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  google_business: "Google Business Profile",
  gbp: "Google Business Profile",
  pinterest: "Pinterest",
  youtube: "YouTube",
  linkedin: "LinkedIn",
  tiktok: "TikTok"
};
const isReviewPlatform = p => ["gbp", "google_business"].includes(String(p || "").toLowerCase());
const platformLabel = p => PLATFORM_LABELS[String(p || "").toLowerCase()] || p || "—";
const TABS = [["overview", "Overview"], ["platforms", "Platforms"], ["posts", "Posts"], ["reviews", "Reviews"]];
class SocialViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onRange", token => this.props.history.push(`/Social/${token}`));
    _defineProperty(this, "openTab", key => {
      this.setState({
        tab: key
      });
      if (key === "posts" && this.state.tabPosts === undefined) this.loadTabPosts(this.state.postsSort);
    });
    // §306.1: posts for the Posts tab, sorted server-side.
    _defineProperty(this, "loadTabPosts", async sort => {
      this.setState({
        postsSort: sort,
        tabPosts: undefined
      });
      const rows = await safe(svc(`/social/posts/${this.range()}?$top=50&$sort=${sort}`).getAsync(), {
        items: []
      });
      if (this._alive) this.setState(st => st.postsSort === sort ? {
        tabPosts: rows.items || []
      } : null);
    });
    this.state = {
      data: null,
      error: false,
      tab: "overview",
      reviews: undefined,
      businessModel: "manufacturing",
      placeNames: {},
      // §306.1: the Posts TAB is chronological ($sort=recent) with a
      // Top · Recent control; the Overview's "Top posts" keeps the
      // endpoint default (top). Fetched separately, lazily.
      postsSort: "recent",
      tabPosts: undefined
    };
    this._alive = true;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange("this_month");
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    this.load();
    // §219: location-type vocabulary keys off businessModel (Store vs
    // Location — never "showroom"; the suite serves retail AND SaaS).
    resolveBusinessModel().then(bm => this._alive && this.setState({
      businessModel: bm
    })).catch(() => {});
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range) return;
      const target = `/Social/${range}`;
      if (window.location.pathname === target) {
        this.setState({
          data: null,
          error: false,
          reviews: undefined,
          tabPosts: undefined
        }, () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
    // §218-add-2 item 3: map raw location_ref slugs to display names
    // when a managed place matches (by normalized name).
    svc("/location/places").getAsync().then(rows => {
      if (!this._alive) return;
      const norm = v => String(v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const map = {};
      (Array.isArray(rows) ? rows : rows?.items || []).forEach(pl => {
        const name = pl.name || pl.Name;
        if (name) map[norm(name)] = name;
      });
      this.setState({
        placeNames: map
      });
    }).catch(() => {/* raw refs render as-is */});
  }
  locName(ref) {
    const raw = String(ref || "");
    const norm = raw.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    return this.state.placeNames[norm] || this.state.placeNames[norm.replace(/^showroom-/, "")] || raw;
  }
  locTypeLabel() {
    return this.state.businessModel === "retail" ? "store" : "location";
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const prevRange = prev.params && prev.params.range || "";
    const curRange = this.props.params && this.props.params.range || "";
    if (prevRange !== curRange) {
      this.setState({
        data: null,
        error: false,
        reviews: undefined,
        tabPosts: undefined
      });
      this.load();
    }
  }
  async load() {
    const range = this.range();
    try {
      const [accounts, overview, platforms, posts, reviewLocs] = await Promise.all([safe(svc("/social/accounts").getAsync(), {
        items: []
      }), safe(svc(`/social/overview/${range}`).getAsync(), null), safe(svc(`/social/platforms/${range}`).getAsync(), {
        items: []
      }), safe(svc(`/social/posts/${range}?$top=25`).getAsync(), {
        items: []
      }), safe(svc(`/social/reviews/locations/${range}`).getAsync(), {
        items: []
      })]);
      if (!this._alive) return;
      this.setState({
        data: {
          accounts: accounts.items || [],
          overview,
          platforms: platforms.items || [],
          posts: posts.items || [],
          reviewLocs: reviewLocs.items || []
        }
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  loadReviews() {
    if (this.state.reviews !== undefined) return;
    this.setState({
      reviews: null
    });
    svc("/social/reviews?$top=100").getAsync().then(r => {
      if (this._alive) this.setState({
        reviews: r && r.items || []
      });
    }).catch(() => {
      if (this._alive) this.setState({
        reviews: []
      });
    });
  }
  // ---------------------------------------------------------------- pieces

  // Token-death rule (spec §2): a broken connection is a notice at the top, never silently stale data.
  renderConnectionHealth(accounts) {
    const broken = accounts.filter(a => String(g(a, "status") || "").toLowerCase() !== "connected");
    if (!broken.length) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-notice warning"
    }, broken.map((a, i) => /*#__PURE__*/React.createElement("p", {
      key: i,
      style: {
        margin: i ? "4px 0 0" : 0
      }
    }, /*#__PURE__*/React.createElement("b", null, platformLabel(g(a, "platform"))), " connection ", String(g(a, "status") || "broken"), g(a, "lastSync") ? ` · data paused since ${String(g(a, "lastSync")).slice(0, 10)}` : " · data paused", ". Reconnect it under ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Manage Data \u203A Connect"), ".")));
  }
  ribbon(o, ghost) {
    const delta = Number(g(o, "followersDelta")) || 0;
    const v = x => ghost ? "—" : x;
    return /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: [{
        l: "Followers",
        v: v(num(g(o, "followers"))),
        s: ghost ? null : delta ? `${delta > 0 ? "+" : ""}${num(delta)} this range` : "across connected accounts"
      }, {
        l: "Engagement rate",
        v: v(engRate(o)),
        s: ghost ? null : "engagements ÷ reach, on accounts that report reach"
      }, {
        l: "Reach",
        v: v(num(g(o, "reach"))),
        s: ghost ? null : "people who saw a post"
      }, {
        l: "Impressions",
        v: v(num(g(o, "impressions"))),
        s: ghost ? null : "times posts were shown"
      }, {
        l: "Video views",
        v: v(num(g(o, "videoViews"))),
        s: ghost ? null : "YouTube, TikTok, Reels"
      }, {
        l: "Reviews",
        v: v(num(g(o, "reviewCount"))),
        s: ghost ? null : Number(g(o, "avgReviewRating")) > 0 ? `${stars(g(o, "avgReviewRating"))} average` : "no ratings yet"
      }]
    });
  }
  renderOverview(d) {
    const o = d.overview || {};
    const followerRows = d.platforms.filter(p => Number(g(p, "followers")) > 0).map(p => ({
      name: platformLabel(g(p, "platform")),
      value: Number(g(p, "followers")) || 0,
      right: engRate(p)
    }));
    return /*#__PURE__*/React.createElement("div", null, this.ribbon(o), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Platform breakdown",
      hint: "followers \xB7 engagement \xB7 reach \xB7 video views",
      span: followerRows.length > 1 ? 8 : 12
    }, d.platforms.length ? this.platformTable(d.platforms) : /*#__PURE__*/React.createElement(Empty, null, "No platform data in this range yet: the first sync lands overnight after an account connects.")), followerRows.length > 1 ? /*#__PURE__*/React.createElement(Card, {
      title: "Followers by platform",
      hint: "share \xB7 engagement rate",
      span: 4
    }, /*#__PURE__*/React.createElement(HBars, {
      rows: followerRows,
      head: ["Platform", "", "Followers", "Eng. rate"]
    })) : null, /*#__PURE__*/React.createElement(Card, {
      title: "Top posts",
      hint: "by engagement in this range",
      more: "All posts \u2192",
      onMore: () => this.openTab("posts")
    }, this.metricsEpochHint(), d.posts.length ? this.postsTable(d.posts.slice(0, 5)) : /*#__PURE__*/React.createElement(Empty, null, "No posts with metrics in this range.")), d.reviewLocs.length > 0 ? /*#__PURE__*/React.createElement(Card, {
      title: `Reviews by ${this.locTypeLabel()}`,
      hint: "rating \xB7 count \xB7 most recent",
      more: "All reviews \u2192",
      onMore: () => this.openTab("reviews")
    }, this.reviewLocsTable(d.reviewLocs)) : null));
  }
  platformTable(rows) {
    // §218-add-2 item 4: GBP is a reviews platform — zeroed follower/reach columns misread as "broken".
    const o = (this.state.data || {}).overview || {};
    return /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Platform"), /*#__PURE__*/React.createElement("th", null, "Handle"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Followers"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Eng. rate"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Reach"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Video views"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((p, i) => {
      const zeroed = !(Number(g(p, "followers")) > 0) && !(Number(g(p, "reach")) > 0) && !(Number(g(p, "engagements")) > 0);
      if (isReviewPlatform(g(p, "platform")) && zeroed) {
        return /*#__PURE__*/React.createElement("tr", {
          key: i
        }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, platformLabel(g(p, "platform")))), /*#__PURE__*/React.createElement("td", {
          className: "dim"
        }, g(p, "handle") || g(p, "displayName") || "—"), /*#__PURE__*/React.createElement("td", {
          colSpan: 4,
          className: "dim"
        }, "Review platform \xB7 ", num(g(o, "reviewCount")), " reviews \xB7 ", stars(g(o, "avgReviewRating")), " average"));
      }
      return /*#__PURE__*/React.createElement("tr", {
        key: i
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, platformLabel(g(p, "platform")))), /*#__PURE__*/React.createElement("td", {
        className: "dim"
      }, g(p, "handle") || g(p, "displayName") || "—"), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(g(p, "followers"))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, engRate(p)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, reachCell(p)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(g(p, "videoViews"))));
    }))));
  }
  // §306.3: per-post reach/engagement snapshots exist from 2026-09-01 — a window that ENDS before that lists posts without metrics.
  metricsPredateWindow() {
    const r = this.range();
    let end = null;
    if (isCustomRange(r)) {
      const w = parseCustomRange(r);
      end = w ? w[1].toISOString().slice(0, 10) : null;
    } else {
      end = rangeDates(r).endDate;
    }
    return !!end && end <= "2026-09-01";
  }
  metricsEpochHint() {
    if (!this.metricsPredateWindow()) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, "Post metrics are tracked since Sep 1, 2026: this window predates that, so posts list without reach or engagement.");
  }
  postsTable(rows) {
    return /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Post"), /*#__PURE__*/React.createElement("th", null, "Type"), /*#__PURE__*/React.createElement("th", null, "Published"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Reach"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Engagement"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Video views"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((p, i) => {
      const caption = String(g(p, "caption") || "").slice(0, 90) || "—";
      const link = g(p, "permalink");
      return /*#__PURE__*/React.createElement("tr", {
        key: i
      }, /*#__PURE__*/React.createElement("td", {
        style: {
          maxWidth: 420
        }
      }, link ? /*#__PURE__*/React.createElement("a", {
        href: link,
        target: "_blank",
        rel: "noreferrer"
      }, /*#__PURE__*/React.createElement("b", null, caption)) : /*#__PURE__*/React.createElement("b", null, caption), g(p, "platform") ? /*#__PURE__*/React.createElement("span", {
        className: "dim path"
      }, platformLabel(g(p, "platform"))) : null), /*#__PURE__*/React.createElement("td", {
        className: "dim",
        style: {
          textTransform: "capitalize"
        }
      }, g(p, "type") || "—"), /*#__PURE__*/React.createElement("td", {
        className: "dim"
      }, g(p, "publishedDate") ? String(g(p, "publishedDate")).slice(0, 10) : "—"), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(g(p, "reach"))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(g(p, "engagements") != null ? g(p, "engagements") : g(p, "engagement"))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(g(p, "videoViews"))));
    }))));
  }
  reviewLocsTable(rows) {
    const typeLabel = this.locTypeLabel();
    return /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      style: {
        textTransform: "capitalize"
      }
    }, typeLabel), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Rating"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Reviews"), /*#__PURE__*/React.createElement("th", null, "Most recent"))), /*#__PURE__*/React.createElement("tbody", null, rows.map((r, i) => {
      // §218-add-2 item 2: the rollup serializes `latestReviewDate` (SocialReviewLocationModel).
      const recent = g(r, "latestReviewDate") || g(r, "lastReviewDate");
      return /*#__PURE__*/React.createElement("tr", {
        key: i
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, this.locName(g(r, "locationRef") || g(r, "location")) || "—")), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, stars(g(r, "avgRating") != null ? g(r, "avgRating") : g(r, "rating"))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(g(r, "reviewCount") != null ? g(r, "reviewCount") : g(r, "count"))), /*#__PURE__*/React.createElement("td", {
        className: "dim"
      }, recent ? String(recent).slice(0, 10) : "—"));
    }))));
  }
  renderPlatforms(d) {
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Connected accounts",
      hint: "the connections registry lives on Manage Data",
      more: "Manage connections \u2192",
      onMore: () => this.props.history.push("/Data/Connect")
    }, d.accounts.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, "No accounts connected.") : /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Platform"), /*#__PURE__*/React.createElement("th", null, "Handle"), /*#__PURE__*/React.createElement("th", null, "Name"), /*#__PURE__*/React.createElement("th", null, "Status"), /*#__PURE__*/React.createElement("th", null, "Last sync"))), /*#__PURE__*/React.createElement("tbody", null, d.accounts.map((a, i) => {
      const status = String(g(a, "status") || "").toLowerCase();
      return /*#__PURE__*/React.createElement("tr", {
        key: i
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, platformLabel(g(a, "platform")))), /*#__PURE__*/React.createElement("td", {
        className: "dim"
      }, g(a, "handle") || "—"), /*#__PURE__*/React.createElement("td", null, g(a, "displayName") || "—"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
        tone: status === "connected" ? "success" : "warning"
      }, status || "unknown")), /*#__PURE__*/React.createElement("td", {
        className: "dim"
      }, g(a, "lastSync") ? String(g(a, "lastSync")).slice(0, 16).replace("T", " ") : "—"));
    }))))), d.platforms.length > 0 ? /*#__PURE__*/React.createElement(Card, {
      title: "Per-platform performance",
      hint: "this range"
    }, this.platformTable(d.platforms)) : null);
  }
  renderPosts() {
    const {
      tabPosts,
      postsSort,
      postQuery
    } = this.state;
    const q = String(postQuery || "").toLowerCase();
    const rows = (tabPosts || []).filter(p => !q || String(g(p, "caption") || "").toLowerCase().includes(q));
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Posts",
      hint: tabPosts ? `${num(rows.length)} in this range` : ""
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar"
    }, /*#__PURE__*/React.createElement(Segmented, {
      options: [["top", "Top"], ["recent", "Recent"]],
      value: postsSort,
      onChange: k => this.loadTabPosts(k)
    }), /*#__PURE__*/React.createElement(Filters, null, /*#__PURE__*/React.createElement(Search, {
      value: postQuery || "",
      onChange: v => this.setState({
        postQuery: v
      }),
      placeholder: "Search captions"
    }))), this.metricsEpochHint(), tabPosts === undefined ? /*#__PURE__*/React.createElement(Pending, null) : !rows.length ? /*#__PURE__*/React.createElement(Empty, null, tabPosts && tabPosts.length ? "No post matches." : "No posts in this range.") : this.postsTable(rows.slice(0, 50))));
  }
  renderReviews(d) {
    this.loadReviews();
    const {
      reviews
    } = this.state;
    return /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, d.reviewLocs.length > 0 ? /*#__PURE__*/React.createElement(Card, {
      title: `Reviews by ${this.locTypeLabel()}`,
      hint: "rating \xB7 count \xB7 most recent",
      span: 5
    }, this.reviewLocsTable(d.reviewLocs)) : null, /*#__PURE__*/React.createElement(Card, {
      title: "Recent reviews",
      hint: "newest first",
      span: d.reviewLocs.length > 0 ? 7 : 12
    }, reviews === null || reviews === undefined ? /*#__PURE__*/React.createElement(Pending, null) : reviews.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, "No reviews collected yet.") : /*#__PURE__*/React.createElement("ul", {
      className: "wa-list"
    }, reviews.slice(0, 50).map((r, i) => /*#__PURE__*/React.createElement("li", {
      key: i,
      style: {
        gridTemplateColumns: "1fr auto",
        alignItems: "start"
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        minWidth: 0
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: "t"
    }, /*#__PURE__*/React.createElement(Pill, {
      tone: Number(g(r, "rating")) >= 4 ? "success" : Number(g(r, "rating")) >= 3 ? "warning" : "danger"
    }, stars(g(r, "rating"))), " ", g(r, "author") || "Anonymous", g(r, "locationRef") ? /*#__PURE__*/React.createElement("span", {
      className: "p"
    }, this.locName(g(r, "locationRef"))) : null), g(r, "text") ? /*#__PURE__*/React.createElement("div", {
      className: "dim",
      style: {
        whiteSpace: "normal",
        marginTop: 4
      }
    }, g(r, "text")) : null), /*#__PURE__*/React.createElement("div", {
      className: "num"
    }, /*#__PURE__*/React.createElement("small", null, g(r, "reviewDate") ? String(g(r, "reviewDate")).slice(0, 10) : "")))))));
  }

  // §218 addendum: the empty state previews the section instead of a bare sentence — ghosted panels with the connect copy as a notice.
  renderGhostState() {
    const typeLabel = this.locTypeLabel();
    let selfService = false;
    try {
      selfService = adBillingMode() !== "managed";
    } catch (e) {
      selfService = false;
    }
    const bar = w => /*#__PURE__*/React.createElement("span", {
      style: {
        display: "inline-block",
        width: w,
        height: 10,
        borderRadius: 5,
        background: "var(--plk-surface-muted, #eef0f3)"
      }
    });
    const ghostRow = cells => /*#__PURE__*/React.createElement("tr", null, cells.map((w, i) => /*#__PURE__*/React.createElement("td", {
      key: i,
      className: i > 0 ? "n" : ""
    }, bar(w))));
    const ghost = {
      opacity: 0.55,
      pointerEvents: "none",
      userSelect: "none"
    };
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, selfService ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      style: {
        margin: 0
      }
    }, /*#__PURE__*/React.createElement("b", null, "No social accounts are connected to this workspace yet."), " Connect Facebook and Instagram (one login), YouTube, TikTok and Google Business Profile: the first sync lands overnight and this page fills in with your real numbers."), /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginTop: 10,
        marginBottom: 0
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Connect accounts"), /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "Manage Data \u203A Connect \u203A Provider connections"))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      style: {
        margin: 0
      }
    }, /*#__PURE__*/React.createElement("b", null, "No social accounts are connected to this workspace yet."), " The Plunk team connects your accounts for managed workspaces; once the first sync lands, this page fills in with your real numbers."), /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar",
      style: {
        marginTop: 10,
        marginBottom: 0
      }
    }, ["Instagram", "Facebook", "Google Business Profile"].map(pl => /*#__PURE__*/React.createElement(Pill, {
      key: pl,
      tone: "neutral"
    }, pl, " \xB7 connected by the Plunk team"))))), /*#__PURE__*/React.createElement("div", {
      style: ghost,
      "aria-hidden": "true"
    }, this.ribbon({}, true), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement(Card, {
      title: "Platform breakdown",
      span: 8
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Platform"), /*#__PURE__*/React.createElement("th", null, "Handle"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Followers"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Eng. rate"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Reach"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Video views"))), /*#__PURE__*/React.createElement("tbody", null, ["Instagram", "Facebook", "Google Business Profile"].map(pl => /*#__PURE__*/React.createElement("tr", {
      key: pl
    }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("b", null, pl)), /*#__PURE__*/React.createElement("td", null, bar(90)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, bar(50)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, bar(36)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, bar(56)), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, bar(44)))))))), /*#__PURE__*/React.createElement(Card, {
      title: "Followers by platform",
      span: 4
    }, /*#__PURE__*/React.createElement(HBars, {
      rows: [{
        name: "Instagram",
        value: 3
      }, {
        name: "Facebook",
        value: 2
      }, {
        name: "Google Business Profile",
        value: 1
      }],
      head: ["Platform", "", "Followers", ""]
    })), /*#__PURE__*/React.createElement(Card, {
      title: "Top posts"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Post"), /*#__PURE__*/React.createElement("th", null, "Type"), /*#__PURE__*/React.createElement("th", null, "Published"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Reach"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Engagement"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Video views"))), /*#__PURE__*/React.createElement("tbody", null, ghostRow([220, 48, 64, 50, 44, 44]), ghostRow([180, 48, 64, 50, 44, 44]), ghostRow([200, 48, 64, 50, 44, 44]))))), /*#__PURE__*/React.createElement(Card, {
      title: `Reviews by ${typeLabel}`
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      style: {
        textTransform: "capitalize"
      }
    }, typeLabel), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Rating"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Reviews"), /*#__PURE__*/React.createElement("th", null, "Most recent"))), /*#__PURE__*/React.createElement("tbody", null, ghostRow([140, 40, 36, 64]), ghostRow([120, 40, 36, 64]))))))));
  }
  render() {
    const {
      data,
      error,
      tab
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const range = this.range();
    const noAccounts = data.accounts.length === 0;
    const connected = data.accounts.filter(a => String(g(a, "status") || "").toLowerCase() === "connected").length;
    const platforms = Array.from(new Set(data.accounts.map(a => platformLabel(g(a, "platform")))));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      title: "Social",
      subtitle: `${rangeLabelFor(range, RANGES)} · organic reach, engagement and reviews across connected accounts · synced nightly`,
      chips: [noAccounts ? null : {
        k: "Accounts",
        v: `${num(connected)} connected`,
        title: platforms.join(", ")
      }]
    }), noAccounts ? null : /*#__PURE__*/React.createElement("nav", {
      className: "wa-switch",
      "aria-label": "Social views"
    }, TABS.map(([key, label]) => /*#__PURE__*/React.createElement("a", {
      key: key,
      className: "wa-tab" + (tab === key ? " on" : ""),
      style: {
        cursor: "pointer"
      },
      onClick: () => this.openTab(key)
    }, label))), this.renderConnectionHealth(data.accounts), noAccounts ? this.renderGhostState() : /*#__PURE__*/React.createElement("div", null, tab === "overview" && this.renderOverview(data), tab === "platforms" && this.renderPlatforms(data), tab === "posts" && this.renderPosts(), tab === "reviews" && this.renderReviews(data)));
  }
}
export const SocialStatsViewComponent = withRouter(SocialViewComponent);
export default {
  SocialStatsViewComponent
};